/**
 * BreakGlassPanel - Emergency access request component (DRR-236 Track B)
 *
 * Provides a UI for requesting emergency break-glass access per HIPAA 164.312(a)(2)(ii).
 * All requests are fully audited and reviewed within 24 hours.
 *
 * LIFE-CRITICAL: This component grants elevated access in emergency situations.
 * - Time-limited access tokens
 * - Full audit trail
 * - Mandatory justification
 * - Post-access review required
 *
 * Routes to: requestEmergencyAccess, revokeEmergencyAccess, breakGlassWallOverride
 */

import { useState } from 'react';
import {
  Card,
  Stack,
  Title,
  Text,
  Button,
  Modal,
  Textarea,
  Select,
  NumberInput,
  Group,
  Alert,
  Badge,
  ThemeIcon,
  Paper,
  Divider,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconShieldOff,
  IconCheck,
  IconX,
  IconKey,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';

import { useAuth, useTenantId } from '@/auth/AuthContext';

/**
 * Inline GraphQL mutations (codegen Documents not generating for this file)
 */
const REQUEST_EMERGENCY_ACCESS_MUTATION = gql`
  mutation RequestEmergencyAccess($input: EmergencyAccessInput!) {
    requestEmergencyAccess(input: $input) {
      accessToken
      incidentId
      expiresAt
      durationHours
      scenario
    }
  }
`;

const REVOKE_EMERGENCY_ACCESS_MUTATION = gql`
  mutation RevokeEmergencyAccess($tenantId: String!, $incidentId: String!) {
    revokeEmergencyAccess(tenantId: $tenantId, incidentId: $incidentId) {
      success
      incidentId
      revokedAt
      revokedBy
      revokedByName
    }
  }
`;

const BREAK_GLASS_WALL_OVERRIDE_MUTATION = gql`
  mutation BreakGlassWallOverride($input: BreakGlassOverrideInput!) {
    breakGlassWallOverride(input: $input) {
      granted
      overrideToken
      expiresAt
      auditEventId
      notificationSent
      denialReason
    }
  }
`;

/**
 * Emergency scenario options - validated against tenant policy
 */
const EMERGENCY_SCENARIOS = [
  { value: 'medical_emergency', label: 'Medical Emergency' },
  { value: 'regulatory_investigation', label: 'Regulatory Investigation' },
  { value: 'legal_discovery', label: 'Legal Discovery' },
  { value: 'security_incident', label: 'Security Incident' },
  { value: 'system_recovery', label: 'System Recovery' },
  { value: 'patient_safety', label: 'Patient Safety Concern' },
  { value: 'fraud_investigation', label: 'Fraud Investigation' },
  { value: 'other', label: 'Other (specify in justification)' },
];

/**
 * Target scopes for wall override
 */
const WALL_OVERRIDE_SCOPES = [
  { value: 'clinical:read', label: 'Clinical Read (view patient data)' },
  { value: 'billing:read', label: 'Billing Read (view financial data)' },
  { value: 'admin:read', label: 'Admin Read (view system config)' },
  { value: 'audit:read', label: 'Audit Read (view audit logs)' },
];

interface ActiveAccess {
  accessToken: string;
  incidentId: string;
  expiresAt: string;
  scenario: string;
}

interface RequestEmergencyAccessResponse {
  requestEmergencyAccess: {
    accessToken: string;
    incidentId: string;
    expiresAt: string;
    durationHours: number;
    scenario: string;
  };
}

interface RevokeEmergencyAccessResponse {
  revokeEmergencyAccess: {
    success: boolean;
    incidentId: string;
    revokedAt: string;
    revokedBy: string;
    revokedByName: string;
  };
}

interface BreakGlassWallOverrideResponse {
  breakGlassWallOverride: {
    granted: boolean;
    overrideToken: string | null;
    expiresAt: string | null;
    auditEventId: string;
    notificationSent: boolean;
    denialReason: string | null;
  };
}

export function BreakGlassPanel() {
  const { user } = useAuth();
  const tenantId = useTenantId();

  // Modal states
  const [requestOpened, { open: openRequest, close: closeRequest }] = useDisclosure(false);
  const [wallOpened, { open: openWall, close: closeWall }] = useDisclosure(false);
  const [revokeOpened, { open: openRevoke, close: closeRevoke }] = useDisclosure(false);

  // Form states
  const [scenario, setScenario] = useState<string>('');
  const [justification, setJustification] = useState('');
  const [durationHours, setDurationHours] = useState<number>(1);
  const [targetScope, setTargetScope] = useState<string>('');
  const [witnessActorId, setWitnessActorId] = useState('');

  // Active access tracking (in production, this would be fetched from backend)
  const [activeAccess, setActiveAccess] = useState<ActiveAccess | null>(null);

  // Mutations
  const [requestAccess, { loading: requestLoading }] = useMutation<RequestEmergencyAccessResponse>(
    REQUEST_EMERGENCY_ACCESS_MUTATION
  );

  const [revokeAccess, { loading: revokeLoading }] = useMutation<RevokeEmergencyAccessResponse>(
    REVOKE_EMERGENCY_ACCESS_MUTATION
  );

  const [breakGlassOverride, { loading: overrideLoading }] =
    useMutation<BreakGlassWallOverrideResponse>(BREAK_GLASS_WALL_OVERRIDE_MUTATION);

  const resetForm = () => {
    setScenario('');
    setJustification('');
    setDurationHours(1);
    setTargetScope('');
    setWitnessActorId('');
  };

  const handleRequestAccess = async () => {
    if (!scenario || !justification.trim() || !tenantId || !user) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fill in all required fields',
        color: 'red',
      });
      return;
    }

    try {
      const result = await requestAccess({
        variables: {
          input: {
            tenantId,
            userId: user.id,
            userName: user.displayName || user.email || 'Unknown',
            scenario,
            justification,
            durationHours,
          },
        },
      });

      if (result.data?.requestEmergencyAccess) {
        const access = result.data.requestEmergencyAccess;
        setActiveAccess({
          accessToken: access.accessToken,
          incidentId: access.incidentId,
          expiresAt: access.expiresAt,
          scenario: access.scenario,
        });

        notifications.show({
          title: 'Emergency Access Granted',
          message: `Access granted until ${new Date(access.expiresAt).toLocaleString()}`,
          color: 'orange',
          icon: <IconKey size={16} />,
        });

        closeRequest();
        resetForm();
      }
    } catch (error) {
      notifications.show({
        title: 'Request Failed',
        message: error instanceof Error ? error.message : 'Failed to request emergency access',
        color: 'red',
      });
    }
  };

  const handleRevokeAccess = async () => {
    if (!activeAccess || !tenantId) return;

    try {
      const result = await revokeAccess({
        variables: {
          tenantId,
          incidentId: activeAccess.incidentId,
        },
      });

      if (result.data?.revokeEmergencyAccess?.success) {
        setActiveAccess(null);
        notifications.show({
          title: 'Access Revoked',
          message: 'Emergency access has been revoked',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        closeRevoke();
        resetForm();
      }
    } catch (error) {
      notifications.show({
        title: 'Revocation Failed',
        message: error instanceof Error ? error.message : 'Failed to revoke access',
        color: 'red',
      });
    }
  };

  const handleWallOverride = async () => {
    if (!targetScope || !justification.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a scope and provide justification',
        color: 'red',
      });
      return;
    }

    try {
      const result = await breakGlassOverride({
        variables: {
          input: {
            targetScope,
            justification,
            witnessActorId: witnessActorId || undefined,
          },
        },
      });

      const override = result.data?.breakGlassWallOverride;
      if (override?.granted) {
        notifications.show({
          title: 'Wall Override Granted',
          message: `Access to ${targetScope} granted${override.expiresAt ? ` until ${new Date(override.expiresAt).toLocaleString()}` : ''}`,
          color: 'orange',
          icon: <IconShieldOff size={16} />,
        });
        closeWall();
        resetForm();
      } else {
        notifications.show({
          title: 'Override Denied',
          message: override?.denialReason || 'Wall override request was denied',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Override Failed',
        message: error instanceof Error ? error.message : 'Failed to request wall override',
        color: 'red',
      });
    }
  };

  return (
    <>
      <Stack>
        {/* Warning Banner */}
        <Alert
          color="orange"
          title="Emergency Access Controls"
          icon={<IconAlertTriangle size={20} />}
        >
          <Text size="sm">
            Emergency access bypasses normal access controls. All requests are logged, audited, and
            reviewed within 24 hours. Misuse may result in disciplinary action.
          </Text>
        </Alert>

        {/* Active Access Status */}
        {activeAccess && (
          <Card shadow="sm" padding="lg" withBorder style={{ borderColor: 'var(--mantine-color-orange-6)' }}>
            <Group justify="space-between" mb="md">
              <Group>
                <ThemeIcon color="orange" size="lg">
                  <IconKey size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600}>Active Emergency Access</Text>
                  <Text size="sm" c="dimmed">
                    Expires: {new Date(activeAccess.expiresAt).toLocaleString()}
                  </Text>
                </div>
              </Group>
              <Button
                color="red"
                variant="outline"
                leftSection={<IconX size={16} />}
                onClick={openRevoke}
              >
                Revoke Access
              </Button>
            </Group>

            <Text size="sm" fw={500} mb="xs">
              Scenario: <Badge color="orange">{activeAccess.scenario}</Badge>
            </Text>

            <Text size="xs" c="dimmed" mt="md">
              Incident ID: {activeAccess.incidentId}
            </Text>
          </Card>
        )}

        {/* Action Cards */}
        <Group grow>
          {/* Request Emergency Access */}
          <Card shadow="sm" padding="lg" withBorder>
            <Stack>
              <Group>
                <ThemeIcon color="red" size="lg" variant="light">
                  <IconAlertTriangle size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600}>Request Emergency Access</Text>
                  <Text size="xs" c="dimmed">
                    Time-limited elevated permissions
                  </Text>
                </div>
              </Group>
              <Text size="sm">
                Request temporary elevated access for emergency situations. All requests require
                justification and are subject to post-access review.
              </Text>
              <Button
                color="red"
                leftSection={<IconKey size={16} />}
                onClick={openRequest}
                disabled={!!activeAccess}
              >
                Request Access
              </Button>
            </Stack>
          </Card>

          {/* Wall Override */}
          <Card shadow="sm" padding="lg" withBorder>
            <Stack>
              <Group>
                <ThemeIcon color="orange" size="lg" variant="light">
                  <IconShieldOff size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600}>Wall Override</Text>
                  <Text size="xs" c="dimmed">
                    Cross-boundary access (DRR-123)
                  </Text>
                </div>
              </Group>
              <Text size="sm">
                Break through clinical/operational information barriers. Requires Tier 4
                verification for some scopes.
              </Text>
              <Button
                color="orange"
                leftSection={<IconShieldOff size={16} />}
                onClick={openWall}
              >
                Request Override
              </Button>
            </Stack>
          </Card>
        </Group>

        {/* Compliance Notice */}
        <Paper p="md" withBorder bg="gray.0">
          <Text size="xs" c="dimmed">
            <strong>HIPAA 164.312(a)(2)(ii)</strong>: Procedures for obtaining necessary electronic
            protected health information during an emergency. All emergency access is logged per
            HIPAA audit requirements and reviewed by the compliance team within 24 hours.
          </Text>
        </Paper>
      </Stack>

      {/* Request Access Modal */}
      <Modal
        opened={requestOpened}
        onClose={closeRequest}
        title={
          <Group>
            <ThemeIcon color="red">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <Text fw={600}>Request Emergency Access</Text>
          </Group>
        }
        size="lg"
      >
        <Stack>
          <Alert color="orange" variant="light">
            Emergency access is logged and reviewed. Provide accurate justification.
          </Alert>

          <Select
            label="Emergency Scenario"
            placeholder="Select scenario type"
            data={EMERGENCY_SCENARIOS}
            value={scenario}
            onChange={(val) => setScenario(val || '')}
            required
          />

          <Textarea
            label="Justification"
            placeholder="Describe the emergency situation and why elevated access is required..."
            minRows={4}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            required
          />

          <NumberInput
            label="Duration (hours)"
            description="Maximum 24 hours"
            min={1}
            max={24}
            value={durationHours}
            onChange={(val) => setDurationHours(typeof val === 'number' ? val : 1)}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeRequest}>
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<IconKey size={16} />}
              onClick={handleRequestAccess}
              loading={requestLoading}
            >
              Request Access
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Wall Override Modal */}
      <Modal
        opened={wallOpened}
        onClose={closeWall}
        title={
          <Group>
            <ThemeIcon color="orange">
              <IconShieldOff size={20} />
            </ThemeIcon>
            <Text fw={600}>Wall Override Request</Text>
          </Group>
        }
        size="lg"
      >
        <Stack>
          <Alert color="orange" variant="light">
            Wall overrides bypass information barriers. This action is audited at Tier 1.
          </Alert>

          <Select
            label="Target Scope"
            placeholder="Select scope to access"
            data={WALL_OVERRIDE_SCOPES}
            value={targetScope}
            onChange={(val) => setTargetScope(val || '')}
            required
          />

          <Textarea
            label="Justification"
            placeholder="Explain why cross-boundary access is necessary..."
            minRows={3}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            required
          />

          <Textarea
            label="Witness Actor ID (optional)"
            description="Required for Tier 4 scopes"
            placeholder="Enter witness actor ID for fresh witness verification"
            value={witnessActorId}
            onChange={(e) => setWitnessActorId(e.target.value)}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeWall}>
              Cancel
            </Button>
            <Button
              color="orange"
              leftSection={<IconShieldOff size={16} />}
              onClick={handleWallOverride}
              loading={overrideLoading}
            >
              Request Override
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Revoke Access Modal */}
      <Modal
        opened={revokeOpened}
        onClose={closeRevoke}
        title={
          <Group>
            <ThemeIcon color="red">
              <IconX size={20} />
            </ThemeIcon>
            <Text fw={600}>Revoke Emergency Access</Text>
          </Group>
        }
      >
        <Stack>
          <Text>
            Are you sure you want to revoke your emergency access? This action is immediate and
            cannot be undone.
          </Text>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeRevoke}>
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<IconX size={16} />}
              onClick={handleRevokeAccess}
              loading={revokeLoading}
            >
              Revoke Access
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default BreakGlassPanel;
